/**
 * Slots the Lanes tab leaves for the bark package (docs/ux/project-tab-spec.md §1.2, §1.3, P1-bark):
 * the bark box, the live outcome feed ("Your requests") and the bark inspector.
 *
 * The bark package registers its components once at module load:
 *
 *   provideLanesSlots({ BarkBox, OutcomeFeed, BarkInspector })
 *
 * and imports that module from `routes/project-tab.tsx`. Until then the defaults below keep the
 * tab usable: the box hands the text to the existing capture sheet, and the feed lists this
 * project's legacy captures read-only (§3.6).
 *
 * Contract the BarkBox must keep: its textarea carries `data-primary-input` (so `/` and `b` focus
 * it), it is disabled with "Unlock to send" when `disabled`, and it docks itself nowhere — the tab
 * places it (top-left on desktop, docked at the bottom on mobile).
 */
import { Button } from '@viko/transcript-ui'
import { Lightbulb, Send } from 'lucide-react'
import {
  type ComponentType,
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useIdeas } from '../../lib/queries.js'
import type { InboxItem } from '../../lib/types.js'
import { useAppUi } from '../app-ui.js'
import { StaticRouteChip } from '../bark/RouteChip.js'
import { Ago } from '../ui.js'

export interface BarkBoxSlotProps {
  projectId: string
  projectName: string
  /** `large` on an empty project. */
  size: 'normal' | 'large'
  /** Starter text picked from the empty state; `nonce` changes on every pick. */
  prefill?: { text: string; nonce: number }
  /** Locked or no content key: render disabled with "Unlock to send". */
  disabled: boolean
  /** The inspector shows this live run: offer "Send straight to this run instead" (§1.2). */
  inspectRunId?: string
}

export interface OutcomeFeedSlotProps {
  projectId: string
  projectName: string
  /** Rows to show in the rail (the full feed opens through `onInspect('barks')`). */
  limit: number
  /** Opens an inspector (`bark:<id>`, `task:<id>`, …) in this tab. */
  onInspect: (key: string) => void
}

export interface BarkInspectorSlotProps {
  projectId: string
  barkId: string
  onInspect: (key: string) => void
}

// ---- now-inbox: the real decision view and an asks panel over the Inbox's own rows ----
export interface DecisionInspectorSlotProps {
  /** `ask:<id>` · `decision:<id>` · `batch:<session>`. */
  inspectKey: string
  projectId: string
  /** The hub ask behind an `ask:` key (the tab already holds it). */
  item?: InboxItem
  onInspect: (key: string) => void
  /** Shown when the key does not resolve (fixtures, answered meanwhile). */
  fallback: ReactNode
}
export interface AsksPanelSlotProps {
  projectId: string
  /** The inspected item (the panel marks its row selected). */
  inspectKey?: string
  /** The asks the tab's model counted (the panel falls back when it cannot show them). */
  askIds: readonly string[]
  onInspect: (key: string) => void
  fallback: ReactNode
  /** The Outside Viko row (work outside Viko to review), rendered last in the section. */
  outside?: ReactNode
}
// ---- end now-inbox ----

export interface LanesSlots {
  BarkBox: ComponentType<BarkBoxSlotProps>
  OutcomeFeed: ComponentType<OutcomeFeedSlotProps>
  BarkInspector?: ComponentType<BarkInspectorSlotProps>
  DecisionInspector?: ComponentType<DecisionInspectorSlotProps> // now-inbox
  AsksPanel?: ComponentType<AsksPanelSlotProps> // now-inbox
}

/** Default bark box: writes nothing itself; hands the text to the capture sheet. */
export function CaptureBarkBox({ projectName, size, prefill, disabled }: BarkBoxSlotProps) {
  const ui = useAppUi()
  const [text, setText] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (!prefill) return
    setText(prefill.text)
    ref.current?.focus()
  }, [prefill])
  const submit = (e?: FormEvent) => {
    e?.preventDefault()
    if (!text.trim() || disabled) return
    ui.openCapture({ text: text.trim() })
    setText('')
  }
  const placeholder = disabled
    ? 'Unlock to send'
    : size === 'large'
      ? `What should happen in ${projectName}? A goal, a bug, an idea. Say it however it comes.`
      : `Tell ${projectName} anything: an idea, a bug, a request, a question`
  return (
    <form className="lk-bark" onSubmit={submit} aria-label={`Tell Viko about ${projectName}`}>
      <label className="sr-only" htmlFor="lk-bark-input">
        Tell Viko about {projectName}
      </label>
      <textarea
        id="lk-bark-input"
        ref={ref}
        data-primary-input
        rows={size === 'large' ? 3 : 2}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit()
          if (e.key === 'Escape') e.currentTarget.blur()
        }}
      />
      <div className="lk-bark__row">
        <span className="lk-bark__hint">
          <StaticRouteChip />
        </span>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          icon={<Send size={14} />}
          disabled={disabled || !text.trim()}
        >
          Send
        </Button>
      </div>
    </form>
  )
}

/** Default feed: this project's captures, read-only (legacy barks, §3.6). */
export function LegacyOutcomeFeed({ projectId, limit }: OutcomeFeedSlotProps) {
  const ideas = useIdeas()
  const list = (ideas.data ?? [])
    .filter((i) => i.projectId === projectId && i.status !== 'archived')
    .slice(0, limit)
  // Hidden until something was said: the composer's placeholder carries the explanation.
  if (!list.length) return null
  return (
    <section aria-labelledby="lk-feed-h">
      <div className="lk-section-h">
        <h2 id="lk-feed-h">Your requests</h2>
      </div>
      <ul className="m-0 grid list-none gap-2 p-0">
        {list.map((i) => (
          <li key={i.id} className="grid gap-0.5">
            <span className="flex items-baseline gap-2 text-[15px]">
              <Lightbulb size={14} aria-hidden="true" className="flex-none text-text-3" />
              <span className="min-w-0 flex-1">“{i.text || 'Attachment'}”</span>
              <span className="flex-none text-[14px] text-text-3">
                <Ago ts={i.createdAt} />
              </span>
            </span>
            <span className="pl-[22px] text-[14px] text-text-2">
              Captured — sorted by the old brain ({i.status ?? 'new'})
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

const DEFAULTS: LanesSlots = { BarkBox: CaptureBarkBox, OutcomeFeed: LegacyOutcomeFeed }
let current: LanesSlots = DEFAULTS
const listeners = new Set<() => void>()

/** Register the bark package's components (once, at module load). */
export function provideLanesSlots(slots: Partial<LanesSlots>): void {
  current = { ...current, ...slots }
  for (const l of listeners) l()
}

export function useLanesSlots(): LanesSlots {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
  )
}
